import React from "react";
import { useNavigate } from "react-router-dom";
import { Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Persona, SplitButton, Text, makeStyles, mergeClasses } from "@fluentui/react-components";
import { themeTokens } from "../../utils/Theming";
import { ArrowExit20Filled, ChevronDown16Filled, Link20Filled, Open16Filled } from "@fluentui/react-icons";
import { getAvatar, getEntityIcon } from "../../utils/Helpers";
import { useGlobalStyles } from "../../utils/Theming";
import Icon from "../Icon";
import { useEntityExternalUrl } from "../../hooks/useEntityExternalUrl";
import { useLogout } from "../../hooks/useLogout";

const useStyles = makeStyles({
  container: {
    display: "flex",
    alignItems: "center",
    gap: themeTokens.spacingHorizontalM,
    textAlign: "left",
    padding: "20px 20px 0 20px",
    position: "relative",
    backgroundColor: themeTokens.colorNeutralBackground2,
  },
  wrapper: {
    display: "flex",
    alignItems: "center",
    gap: themeTokens.spacingHorizontalM,
    width: "215px",
  },
  menuIcon: {
    '& .fui-MenuButton__menuIcon': {
      width: '16px',
      height: '16px',
    }
  },
  info: {
    flexGrow: 1,
    overflow: "hidden",
  },
  heading: {
    marginTop: 0,
  },
  status: {
    color: themeTokens.colorNeutralForeground1,
  }
});

const TabHeader = ({
  name,
  status,
  profileImage,
  entity,
  entityId,
}: {
  name: string;
  status?: string;
  profileImage?: any;
  entity: string;
  entityId?: string | number;
}) => {
  const styles = useStyles();
  const globalStyles = useGlobalStyles();
  const navigate = useNavigate();
  const logout = useLogout();
  const hasProfileImage = !!profileImage;
  const EntityIcon = getEntityIcon(entity, 24);
  const { url } = useEntityExternalUrl(entity, entityId);

  const handlePrimaryAction = () => {
  if (url) {
    window.open(url, "_blank");
  }
};

  const handleLinkToItems = () => {
    navigate("/link-to-related-items");
  };

  const handleSignOut = async () => {
    await logout();
    navigate("/login", { state: { preventAutoLogin: true } });
  };

  return (
    <div className={styles.container}>
      <>
        {hasProfileImage ? (
          <Persona
            size="huge"
            name={name}
            avatar={getAvatar(profileImage)}
            secondaryText={status}
            textAlignment="center"
          />
        ) : (
          <div className={styles.wrapper}>
            <Icon icon={<EntityIcon />} size="extra-large" />
            <div className={styles.info}>
              <Text
                className={mergeClasses(styles.heading, globalStyles.truncateText)}
                as="h2"
                size={400}
                weight="semibold"
                style={{ margin: 0 }}
                block
              >
                {name}
              </Text>
              <Text className={mergeClasses(styles.status, globalStyles.truncateText)} size={300} block>
                {status}
              </Text>
            </div>
          </div>
        )}
        <div>
          <Menu positioning="below-end">
            <MenuTrigger disableButtonEnhancement>
              {(triggerProps) => (
                <SplitButton
                  className={styles.menuIcon}
                  menuButton={triggerProps}
                  primaryActionButton={{ onClick: handlePrimaryAction }}
                  appearance="secondary"
                  icon={<Open16Filled />}
                  menuIcon={<ChevronDown16Filled />}
                />
              )}
            </MenuTrigger>

            <MenuPopover>
              <MenuList>
                <MenuItem icon={<Link20Filled />} onClick={handleLinkToItems}>
                  Link to item
                </MenuItem>
                <MenuItem icon={<ArrowExit20Filled />} onClick={handleSignOut}>
                  Sign out
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </>
    </div>
  );
};

export default TabHeader;
